<template>
	<div class="page-edit-agent" id="pageEditAgent">
		<div class="agent__wrap">
			<h2 class="section-title agent__title">{{ this.label }} Agent</h2>
			<div class="notification-wrap-side notification-wrap-side--center">
				<PartNotifications :data="notificationsData" v-if="notificationsData.msg" />
			</div>
		</div>
		<ValidationObserver ref="agentForm">
			<form class="agent__form" @submit.prevent="updateAgent">
				<div class="error-messages__list" v-if="errors.length">
					<span class="error-messages__title">
						<SvgIcon class="error-messages__icon error-messages__icon--exclamation" iconName="ico-exclamation-circle"/>
						Please fill in all required fields:
					</span>
					<ul>
						<li class="error-messages__item" v-for="error in errors" :key="error">{{ error }}</li>
					</ul>
				</div>
				<div class="agent__form-half">
					<div class="agent__form-input-box">
						<div class="property-form__input">
							<ValidationProvider rules="required" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
								<PartInputBasic v-model="agent.first_name" :class="classes" ref="first_name" :data="DataFirstName"/>
								<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
							</ValidationProvider>
						</div>

						<div class="property-form__input">
							<ValidationProvider rules="required" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
								<PartInputBasic v-model="agent.last_name" :class="classes" ref="last_name" :data="DataLastName"/>
								<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
							</ValidationProvider>
						</div>
					</div>

					<div class="property-form__input property-form__input--upload-img">
						<BlockUploadImage v-model="agent.photo" ref="photo" :data="DataUploadImage"/>
					</div>
				</div>

				<div class="property-form__input">
					<ValidationProvider rules="required|email" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
						<PartInputBasic v-model="agent.email" :class="classes" ref="email" :data="DataEmailName"/>
						<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
						<span class="error-message" v-else-if="emailExists">{{ emailExistsMessage }}</span>
					</ValidationProvider>
				</div>
				<!-- This field has been newly added for alternative email -->
				<div class="property-form__input">
					<ValidationProvider rules="required|email" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
						<PartInputBasic v-model="agent.alternative_email" :class="classes" ref="alternative_email" :data="DataAlternativeEmail"/>
						<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
						<span class="error-message" v-else-if="alternativeEmailExists">{{ AlternativEmailExistsMessage }}</span>
					</ValidationProvider>
				</div>
				<div class="property-form__input">
					<ValidationProvider :rules="`${LandlinePhone ? 'min:5' : ''}`" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
						<PartInputBasic v-model="agent.landline_phone" :class="classes" ref="landline_phone" :data="DataLandlinePhone"/>
						<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
					</ValidationProvider>
				</div>

				<div class="property-form__input">
					<PartInputBasic v-model="agent.phone_ext" ref="phone_ext" :data="DataPhoneExt"/>
				</div>

				<div class="property-form__input">
					<ValidationProvider rules="required|min:5" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
						<PartInputBasic v-model="agent.cellphone" :class="classes" ref="cellphone" :data="DataCellphone"/>
						<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
					</ValidationProvider>
				</div>

				<div class="agent__btn-wrap">
					<a class="btn agent__btn-sm" id="update_agent-button" @click="updateAgent()">
						<SvgIcon class="save-icon" iconName="ico-save"/>
						Save
					</a>
					<a class="btn btn--white btn--icon agent__btn-sm" id="back_agent-button" v-if="data.noAgentPage" @click="closePopup()">
						<span>Cancel</span>
					</a>
					<router-link class="btn btn--white btn--icon agent__btn-sm" id="back_agent-button1" :to="{ name: 'Agents' }" v-if="!data.noAgentPage && !myAccount">
						<span>Cancel</span>
					</router-link>

					<router-link class="btn btn--white btn--icon agent__btn-sm" id="back_agent-button2" :to="{ name: 'CustomersAgent' }" v-if="myAccount && !data.noAgentPage">
						<span>Cancel</span>
					</router-link>
				</div>
			</form>
		</ValidationObserver>
		<transition name="fade" appear>
			<PartLoaderBig v-if="pageLoader"/>
		</transition>
	</div>
</template>

<script>
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { required, email, min } from 'vee-validate/dist/rules';
import PartInputBasic from '@/components/parts/PartInputBasic';
import SvgIcon from '@/components/base/SvgIcon/SvgIcon';
import PartNotifications from '@/components/parts/PartNotifications';
import BlockUploadImage from '@/components/blocks/BlockUploadImage';
import PartLoaderBig from '@/components/parts/PartLoaderBig';

extend('email', {
	...required,
	message: 'This field is mandatory.'
});

extend('email', {
	...email,
	message: 'Email must be in correct format.'
});

extend('required', {
	...required,
	message: 'This field is mandatory.'
});

extend('min', {
	...min,
	message: 'Minimum number of characters is 5.'
});

export default {
	props: {
		edit: Boolean,
		myAccount: Boolean,
		data: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			errors: [],
			pageLoader: false,
			LandlinePhone: false,
			emailExists: false,
			emailExistsMessage: 'This email is already in use!',
			AlternativEmailExistsMessage: 'This alternative email is already in use!',
			alternativeEmailExists: false,
			DataUploadImage: {},
			label: this.edit ? 'Update' : 'Create New',
			notificationsData: {
				type: '',
				msg: ''
			},
			agent: {
				id: this.edit ? this.$route.params.id : 0,
				first_name: '',
				last_name: '',
				role: 'agent',
				email: '',
				landline_phone: '',
				phone_ext: '',
				cellphone: '',
				photo: '',
				alternative_email: ''
			},
			DataFirstName: {
				label: 'First Name',
				placeholder: 'Enter First Name',
				inputId: 'first_name-input',
				mandatoryFiled: true,
				labelId: 'first_name-label'
			},
			DataLastName: {
				label: 'Last Name',
				placeholder: 'Enter Last Name',
				inputId: 'last_name-input',
				mandatoryFiled: true,
				labelId: 'last_name-label'
			},
			DataEmailName: {
				label: 'Email',
				placeholder: 'Enter Email',
				inputId: 'email-input',
				mandatoryFiled: true,
				labelId: 'email-label'
			},
			DataAlternativeEmail: {
				label: 'Alternative Email',
				placeholder: 'Enter Email',
				inputId: 'alternativeEmailAgent-input',
				mandatoryFiled: true,
				labelId: 'alternativeEmailAgent-label'
			},
			DataLandlinePhone: {
				label: 'Landline Phone',
				placeholder: 'Enter Landline Phone',
				inputId: 'landline-input',
				labelId: 'landline-label',
				mandatoryFiled: false,
				type: 'text'
			},
			DataPhoneExt: {
				label: 'Phone Ext',
				placeholder: 'Enter Phone Ext',
				inputId: 'phone_ext-input',
				labelId: 'phone_ext-label',
				mandatoryFiled: false,
				type: 'text'
			},
			DataCellphone: {
				label: 'Cellphone',
				placeholder: 'Enter Cellphone',
				inputId: 'cellphone-input',
				mandatoryFiled: true,
				labelId: 'cellphone-label',
				type: 'text'
			}
		};
	},
	watch: {
		agent: {
			handler(val) {
				this.LandlinePhone = !!val.landline_phone;
			},
			deep: true
		},
		user() {
			return this.$store.getters.getUser;
		},
		test() {
			if (this.agent.alternative_email.length < 1) {
			}
		}
	},
	components: {
		ValidationProvider,
		ValidationObserver,
		PartInputBasic,
		SvgIcon,
		PartNotifications,
		BlockUploadImage,
		PartLoaderBig
	},
	methods: {
		updateAgent(e) {
			this.pageLoader = true;
			const action = this.myAccount ? 'updateMyAccount' : 'updateAgent';
			this.$refs.agentForm.validate().then(success => {
				if (success) {
					this.$store.dispatch(action, this.agent).then(response => {
						return response;
					}).then((response) => {
						if (!this.user) {
							this.user = JSON.parse(localStorage.getItem('user'));
						}
						this.emailExists = false;
						this.alternativeEmail = false;
						if (this.data.noAgentPage) {
							this.setAgent(response.data.agent.id);
							setTimeout(() => {
								this.closePopup();
							}, 2000);
						} else if (this.myAccount) {
							setTimeout(() => {
								this.$router.push({ name: 'CustomersAgent' });
							}, 1000);
						} else {
							setTimeout(() => {
								this.backOnListing();
								this.pageLoader = false;
							}, 1000);
						}

						if (this.myAccount || this.user.id === this.agent.id) {
							// commit change in state for update my account
							this.$store.commit('updateUserAccount', this.agent);
						}
					}).catch((error) => {
						this.pageLoader = false;
						this.errors = [];
						if (error.response.data.errors.email && error.response.data.errors.email[0].includes('The email has already been taken')) {
							this.emailExists = true;
							this.errors.push('The email has already been taken.');
						}
						if (error.response.data.errors.alternative_email && error.response.data.errors.alternative_email[0].includes('The alternative email has already been taken')) {
							this.alternativeEmailExists = true;
							this.errors.push('The alternative email has already been taken.');
						}
					});
				} else {
					this.pageLoader = false;
					// error messages list
					this.createErrorList();
					document.getElementById('pageEditAgent').scrollTop = 0;
				}
			});
		},
		createErrorList() {
			this.errors = [];
			if (this.agent.first_name && this.agent.last_name && this.agent.email &&
			(this.agent.alternative_email && this.validAlternativeEmail(this.agent.alternative_email)) &&
			this.agent.cellphone && this.agent.cellphone && (this.agent.cellphone.length >= 5) && this.agent.alternative_email &&
			(this.agent.email && this.validEmail(this.agent.email))) return true;
			if (!this.agent.first_name) this.errors.push('First Name is required.');
			if (!this.agent.last_name) this.errors.push('Last Name is required.');
			if (!this.agent.cellphone) this.errors.push('Phone is required.');
			if (this.agent.cellphone && this.agent.cellphone.length < 5) this.errors.push('Phone must contain valid information.');
			if (!this.agent.email) {
				this.errors.push('Email is required.');
			} else if (this.agent.email && !this.validEmail(this.agent.email)) {
				this.errors.push('Email must contain valid information.');
			}
			if (!this.agent.alternative_email) {
				this.errors.push('Alternative email is required.');
			} else if (this.agent.alternative_email && !this.validAlternativeEmail(this.agent.alternative_email)) {
				this.errors.push('Alternative email must contain valid information.');
			}
			return false;
		},
		validEmail: function (email) {
			const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			return re.test(email);
		},
		validAlternativeEmail: function (alternativeEmail) {
			const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			return re.test(alternativeEmail);
		},
		notification(msg, status) {
			this.notificationsData.msg = msg;
			this.notificationsData.type = status === 200 ? 'success' : 'danger';

			setTimeout(() => {
				this.notificationsData.msg = '';
				this.notificationsData.type = '';
			}, 2000);
		},
		closePopup() {
			this.$emit('closePopup');
		},
		setAgent(agentId) {
			this.$emit('setAgent', agentId);
		},
		backOnListing() {
			this.$router.push({ name: 'Agents' });
		}
	},
	created() {
		if (this.edit) {
			const action = this.myAccount ? 'editMyAccount' : 'fetchAgent';
			this.$store.dispatch(action, !this.myAccount ? this.agent.id : '').then(response => {
				this.agent = response.data.agent;
				return this.agent;
			}).then(response => {
				for (const key in response) {
					if (this.$refs[key]) {
						this.$refs[key].setValue(this.agent[key]);
					}
				}
			});
		}
	}
};
</script>
<style lang="scss" scoped>
.page-edit-agent {
	padding-top: 15px;
}
.agent__form {
	max-width: 700px;
	padding-bottom: 20px;
}
.agent__btn-wrap {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
	justify-content: flex-start;

	.agent__btn-sm {
		max-width: 147px;

		&:not(:first-child) {
			margin-left: 50px;
		}
		@include mq(400px) {
			max-width: 120px;
			min-width: auto;
		}
	}
}

.save-icon {
	margin-right: 11px;
	svg {
		font-size: 20px;
		color: $white;
	}
}

.agent__title {
	margin-right: 25px;
	display: inline-block;
}
.agent__wrap {
	display: flex;
	align-items: center;
	margin-bottom: 20px;

	@include mq($md) {
		flex-wrap: wrap;
	}
}

.agent__btn-icon {
    display: inline-block;
    padding-right: 7px;
    font-size: 11px;
    height: 14px;
}

.agent__form-half {
	display: flex;

	@include mq($md) {
		flex-direction: column;
	}
}

.agent__form-input-box {
	max-width: 70%;
	flex: 0 0 70%;

	@include mq($md) {
		flex: 0 0 100%;
		max-width: 100%;
		order: 2;
	}
}

.property-form__input--upload-img {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	width: 100%;

	@include mq($md)  {
		justify-content: flex-start;
		margin-bottom: 55px;
	}
}

.property-form__input {
	position: relative;
}

.show-pass {
	position: absolute;
	top: 40px;
    right: 10px;
    transform: none;
	font-size: 14px;
	color: $fiord;
	transition: $dur $ease;

	@include hover {
		color: $dodger-blue;
	}
}

.error-message {
	bottom: -19px;
	position: absolute;
	display: inline-block;
	transform: none;
}
</style>
